import React, { useEffect, useState } from 'react';
import {
  Avatar,
  Box,
  Card,
  CardContent,
  IconButton,
  Slide,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

interface NotificationPayload {
  avatar?: string;
  title?: string;
  message: string;
  onClick?: () => void;
  // Keeping type for backward compatibility, though not used for styling here
  type?: 'success' | 'info' | 'warning' | 'error';
  /**
   * If provided (in milliseconds), the toast will auto-dismiss after this amount
   * of time. If omitted or 0/negative, the toast remains until the user closes it.
   */
  duration?: number;
}

interface NotificationItem extends NotificationPayload {
  id: number;
}

const NotificationStack: React.FC = () => {
  const [items, setItems] = useState<NotificationItem[]>([]);

  useEffect(() => {
    const handler = (event: Event) => {
      const { avatar, title = 'Notification', message, onClick, type, duration } =
        (event as CustomEvent<NotificationPayload>).detail;

      const id = Date.now();
      setItems((prev) => [
        ...prev,
        { id, avatar, title, message, onClick, type },
      ]);

      // Auto-dismiss after specified duration (if provided)
      if (typeof duration === 'number' && duration > 0) {
        setTimeout(() => {
          setItems((prev) => prev.filter((n) => n.id !== id));
        }, duration);
      }
    };

    window.addEventListener('app-toast', handler as EventListener);
    return () => window.removeEventListener('app-toast', handler as EventListener);
  }, []);

  const handleClose = (id: number) =>
    setItems((prev) => prev.filter((n) => n.id !== id));

  return (
    <Box sx={{ position: 'fixed', top: 16, right: 16, zIndex: 2000 }}>
      {items.map((item) => (
        <Slide
          key={item.id}
          direction="left"
          in={true}
          mountOnEnter
          unmountOnExit
        >
          <Card
            sx={{
              display: 'flex',
              alignItems: 'center',
              mb: 1.5,
              minWidth: 280,
              boxShadow: 3,
              cursor: item.onClick ? 'pointer' : 'default',
            }}
            onClick={() => {
              if (item.onClick) {
                item.onClick();
              }
              handleClose(item.id);
            }}
          >
            {item.avatar && (
              <Avatar src={item.avatar} sx={{ ml: 1, mr: 1 }} />
            )}
            <CardContent
              sx={{ flex: 1, py: 1, '&:last-child': { pb: 1 } }}
            >
              <Typography variant="subtitle2" noWrap>
                {item.title}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {item.message}
              </Typography>
            </CardContent>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleClose(item.id);
              }}
              sx={{ mr: 0.5 }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Card>
        </Slide>
      ))}
    </Box>
  );
};

export default NotificationStack; 